import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AgentTemplate } from '../types';
import { createWorkspace, getWorkspace, updateWorkspace, listTemplates } from '../api/client';

export function WorkspaceForm() {
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const navigate = useNavigate();
  const isEdit = !!workspaceId;

  const [name, setName] = useState('');
  const [selectedTemplateIds, setSelectedTemplateIds] = useState<string[]>([]);
  const [templates, setTemplates] = useState<AgentTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        const t = await listTemplates();
        setTemplates(t);
        if (workspaceId) {
          const w = await getWorkspace(workspaceId);
          setName(w.name);
          setSelectedTemplateIds(w.templateIds);
        }
      } catch (e: any) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [workspaceId]);

  const toggleTemplate = (id: string) => {
    setSelectedTemplateIds((prev) =>
      prev.includes(id) ? prev.filter((t) => t !== id) : [...prev, id],
    );
  };

  const handleSave = async () => {
    if (!name.trim()) return;
    setSaving(true);
    setError(null);
    try {
      if (isEdit) {
        await updateWorkspace(workspaceId!, { name: name.trim(), templateIds: selectedTemplateIds });
      } else {
        await createWorkspace({ name: name.trim(), templateIds: selectedTemplateIds });
      }
      navigate('/admin/workspaces');
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div style={s.container}><div style={s.muted}>Loading...</div></div>;

  return (
    <div style={s.container}>
      <button style={s.backBtn} onClick={() => navigate('/admin/workspaces')}>&larr; Back</button>
      <h2 style={s.heading}>{isEdit ? 'Edit Workspace' : 'New Workspace'}</h2>

      <div style={s.form}>
        <label style={s.label}>
          Name *
          <input style={s.input} value={name} onChange={(e) => setName(e.target.value)} placeholder="My Workspace" />
        </label>

        <div>
          <span style={{ fontSize: 13, color: '#555', fontWeight: 600 }}>Linked Templates</span>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
            {templates.map((t) => (
              <button
                key={t.id}
                style={{ ...s.chip, ...(selectedTemplateIds.includes(t.id) ? s.chipActive : {}) }}
                onClick={() => toggleTemplate(t.id)}
              >
                {t.name}
              </button>
            ))}
            {templates.length === 0 && <span style={s.muted}>No templates yet</span>}
          </div>
        </div>

        {error && <div style={s.error}>{error}</div>}

        <button style={s.saveBtn} onClick={handleSave} disabled={saving || !name.trim()}>
          {saving ? 'Saving...' : isEdit ? 'Save' : 'Create Workspace'}
        </button>
      </div>
    </div>
  );
}

const s: Record<string, React.CSSProperties> = {
  container: { padding: 20, flex: 1, overflowY: 'auto' },
  backBtn: { background: 'none', border: 'none', color: '#4f46e5', fontSize: 14, cursor: 'pointer', padding: 0, marginBottom: 12 },
  heading: { margin: '0 0 16px', fontSize: 20, color: '#1a1a1a' },
  form: { display: 'flex', flexDirection: 'column', gap: 12 },
  label: { display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13, color: '#555', flex: 1 },
  input: { padding: '8px 12px', borderRadius: 8, border: '1px solid #ddd', fontSize: 14, outline: 'none' },
  saveBtn: { padding: '8px 18px', borderRadius: 8, border: 'none', background: '#4f46e5', color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer', alignSelf: 'flex-start' },
  error: { padding: '8px 12px', background: '#fff0f0', border: '1px solid #fcc', borderRadius: 8, color: '#c00', fontSize: 13 },
  muted: { color: '#888', fontSize: 14 },
  chip: { padding: '4px 10px', borderRadius: 6, border: '1px solid #ddd', background: '#fff', fontSize: 12, cursor: 'pointer', color: '#555' },
  chipActive: { background: '#4f46e5', color: '#fff', borderColor: '#4f46e5' },
};
